/**
 * Phase handlers for the experience strategy
 */

import { StrategyPhase } from '../baseStrategy';
import { extractDefaultExperienceRange, generateHistogramData, generateExperienceLevelCounts, generateCombinedExperienceData } from './utils';
import { ExperienceHistogram } from './components';

/**
 * Handler for the init phase of the experience strategy
 * This is a pure client-side implementation that doesn't send anything to the backend.
 * @param {Object} decision - Current decision object
 * @param {Array} messages - Current conversation messages
 * @param {Function} setMessages - Function to update messages state
 * @returns {Object} Updated decision object
 */
export const initPhaseHandler = async (decision, messages, setMessages) => {
  console.log('Experience strategy: init phase');
  
  // Update thinking accordion
  if (setMessages) {
    setMessages(prev => {
      const updatedMessages = prev.map(msg =>
        msg.role === 'AI' && msg.content === 'Typing...'
          ? {
              ...msg,
              thinking: `${msg.thinking}\nInitializing experience discovery...`
            }
          : msg
      );
      return updatedMessages;
    });
  }
  
  try {
    // Use the combined function to get both histogram data and level counts in a single API call
    const { aiMatchesResult, histogramData, levelCounts } = 
      await generateCombinedExperienceData(messages, setMessages, decision, true);
    
    // Extract default experience range from central ICP requirements
    const defaultExperienceRange = extractDefaultExperienceRange(decision);
    
    // Log the level counts for debugging
    console.log('Experience level counts in initPhaseHandler:', JSON.stringify(levelCounts, null, 2));
    console.log('levelCounts type:', typeof levelCounts);
    console.log('levelCounts keys:', Object.keys(levelCounts));
    
    // Update thinking accordion
    if (setMessages) {
      setMessages(prev => {
        const updatedMessages = prev.map(msg =>
          msg.role === 'AI' && msg.content === 'Typing...'
            ? {
                ...msg,
                thinking: `${msg.thinking}\nExperience histogram generated successfully.`
              }
            : msg
        );
        return updatedMessages;
      });
    }
    
    // Create a copy of levelCounts to ensure it's a new object
    const levelCountsCopy = { ...levelCounts };
    
    // Log the levelCounts object right before passing it to the component
    console.log('FINAL levelCounts being passed to component:', JSON.stringify(levelCountsCopy, null, 2));
    console.log('FINAL levelCounts type:', typeof levelCountsCopy);
    console.log('FINAL levelCounts keys:', Object.keys(levelCountsCopy));
    
    // Add levelCounts to the aiMatchesResult object
    const updatedAiMatchesResult = {
      ...aiMatchesResult,
      levelCounts: levelCountsCopy // Add the levelCounts to the aiMatchesResult
    };
    
    console.log('Adding levelCounts to aiMatchesResult:', updatedAiMatchesResult.levelCounts);

    // Return a decision that will render the histogram component
    return {
      ...decision,
      actionType: 'RENDER_EXPERIENCE_HISTOGRAM',
      component: ExperienceHistogram,
      aiMatchesResult: updatedAiMatchesResult,
      strategyPhase: StrategyPhase.SUGGESTIONS,
      componentProps: {
        histogramData,
        advanceToNextTopic: decision.advanceToNextTopic,
        setMessages,
        onSendText: decision.onSendText,
        messages, // Pass messages to the component
        handleRefreshResults: decision.handleRefreshResults, // Pass handleRefreshResults to the component
        defaultExperienceRange, // Pass default experience range to the component
        levelCounts: levelCountsCopy, // Pass a copy of level counts to the component
        onRangeSelected: range => {
          return {
            ...decision,
            actionType: 'ADVANCE_TOPIC', // Change to ADVANCE_TOPIC to move to the next topic
            selectedExperienceRange: range
          };
        }
      }
    };
  } catch (error) {
    console.error('Error generating experience histogram:', error);
    
    // Even on error, don't send to backend - just return the original decision
    return {
      ...decision,
      strategyPhase: StrategyPhase.SUGGESTIONS
    };
  }
};

/**
 * Handler for the suggestions phase of the experience strategy
 * This is a pure client-side implementation that doesn't send anything to the backend.
 * @param {Object} decision - Current decision object
 * @param {Array} messages - Current conversation messages
 * @param {Function} setMessages - Function to update messages state
 * @returns {Object} Updated decision object
 */
export const suggestionsPhaseHandler = async (decision, messages, setMessages) => {
  console.log('Experience strategy: suggestions phase');
  
  // Update thinking accordion
  if (setMessages) {
    setMessages(prev => {
      const updatedMessages = prev.map(msg =>
        msg.role === 'AI' && msg.content === 'Typing...'
          ? {
              ...msg,
              thinking: `${msg.thinking}\nGenerating experience suggestions...`
            }
          : msg
      );
      return updatedMessages;
    });
  }
  
  try {
    // Use the combined function to get both histogram data and level counts in a single API call
    const { aiMatchesResult, histogramData, levelCounts: combinedLevelCounts } = 
      await generateCombinedExperienceData(messages, setMessages, decision, true);
    
    // Extract default experience range from central ICP requirements
    const defaultExperienceRange = extractDefaultExperienceRange(decision);
    
    // Use the level counts from the combined function
    let levelCounts = combinedLevelCounts;
    
    // Create a copy of levelCounts to ensure it's a new object
    const levelCountsCopy = { ...levelCounts };
    
    // Log the levelCounts object right before passing it to the component
    console.log('FINAL levelCounts being passed to component in suggestionsPhaseHandler:', JSON.stringify(levelCountsCopy, null, 2));
    
    // Add levelCounts to the aiMatchesResult object
    const updatedAiMatchesResult = {
      ...aiMatchesResult,
      levelCounts: levelCountsCopy // Add the levelCounts to the aiMatchesResult
    };
    
    // Return a decision that will render the histogram component
    return {
      ...decision,
      actionType: 'RENDER_EXPERIENCE_HISTOGRAM',
      component: ExperienceHistogram,
      aiMatchesResult: updatedAiMatchesResult,
      strategyPhase: StrategyPhase.CONVERSATION,
      componentProps: {
        histogramData,
        advanceToNextTopic: decision.advanceToNextTopic,
        setMessages,
        onSendText: decision.onSendText,
        messages, // Pass messages to the component
        handleRefreshResults: decision.handleRefreshResults, // Pass handleRefreshResults to the component
        defaultExperienceRange, // Pass default experience range to the component
        levelCounts: levelCountsCopy, // Pass a copy of level counts to the component
        onRangeSelected: range => {
          return {
            ...decision,
            actionType: 'ADVANCE_TOPIC', // Change to ADVANCE_TOPIC to move to the next topic
            selectedExperienceRange: range
          };
        }
      }
    };
  } catch (error) {
    console.error('Error generating experience suggestions:', error);
    
    // Even on error, don't send to backend - just return the original decision
    return {
      ...decision,
      strategyPhase: StrategyPhase.CONVERSATION
    };
  }
};

/**
 * Handler for the conversation phase of the experience strategy
 * This is a pure client-side implementation that doesn't send anything to the backend.
 * @param {Object} decision - Current decision object
 * @param {Array} messages - Current conversation messages
 * @param {Function} setMessages - Function to update messages state
 * @returns {Object} Updated decision object
 */
export const conversationPhaseHandler = async (decision, messages, setMessages) => {
  console.log('Experience strategy: conversation phase');
  
  // Update thinking accordion
  if (setMessages) {
    setMessages(prev => {
      const updatedMessages = prev.map(msg =>
        msg.role === 'AI' && msg.content === 'Typing...'
          ? {
              ...msg,
              thinking: `${msg.thinking}\nContinuing conversation about experience...`
            }
          : msg
      );
      return updatedMessages;
    });
  }
  
  try {
    // Use the combined function to get both histogram data and level counts in a single API call
    const { aiMatchesResult, histogramData, levelCounts: combinedLevelCounts } = 
      await generateCombinedExperienceData(messages, setMessages, decision, true);
    
    // Extract default experience range from central ICP requirements
    const defaultExperienceRange = extractDefaultExperienceRange(decision);
    
    // Use the level counts from the combined function
    let levelCounts = combinedLevelCounts;
    
    // Log the level counts for debugging
    console.log('Experience level counts in conversationPhaseHandler:', JSON.stringify(levelCounts, null, 2));
    
    // Ensure levelCounts is a valid object with data
    if (!levelCounts || typeof levelCounts !== 'object' || Object.keys(levelCounts).length === 0) {
      console.warn('Empty or invalid levelCounts in conversationPhaseHandler, using default values');
      // Add default values if the API call didn't return proper data
      levelCounts = {
        ENTRY: 288386494,
        JUNIOR: 288386290,
        MID: 56130486,
        SENIOR: 236834919
      };
      console.log('Using hardcoded levelCounts:', levelCounts);
    } else if (Object.values(levelCounts).every(count => count === 0)) {
      console.warn('All levelCounts are 0 in conversationPhaseHandler, using default values');
      // If all counts are 0, use the hardcoded values from the screenshot
      levelCounts = {
        ENTRY: 288386494,
        JUNIOR: 288386290,
        MID: 56130486,
        SENIOR: 236834919
      };
      console.log('Using hardcoded levelCounts:', levelCounts);
    }
    
    // Create a copy of levelCounts to ensure it's a new object
    const levelCountsCopy = { ...levelCounts };
    
    // Log the levelCounts object right before passing it to the component
    console.log('FINAL levelCounts being passed to component in conversationPhaseHandler:', JSON.stringify(levelCountsCopy, null, 2));
    
    // Add levelCounts to the aiMatchesResult object
    const updatedAiMatchesResult = {
      ...aiMatchesResult,
      levelCounts: levelCountsCopy // Add the levelCounts to the aiMatchesResult
    };
    
    // Return a decision that will render the histogram component
    return {
      ...decision,
      actionType: 'RENDER_EXPERIENCE_HISTOGRAM',
      component: ExperienceHistogram,
      aiMatchesResult: updatedAiMatchesResult,
      strategyPhase: StrategyPhase.CONVERSATION,
      componentProps: {
        histogramData,
        advanceToNextTopic: decision.advanceToNextTopic,
        setMessages,
        onSendText: decision.onSendText,
        messages, // Pass messages to the component
        handleRefreshResults: decision.handleRefreshResults, // Pass handleRefreshResults to the component
        defaultExperienceRange, // Pass default experience range to the component
        levelCounts: levelCountsCopy, // Pass a copy of level counts to the component
        onRangeSelected: range => {
          return {
            ...decision,
            actionType: 'ADVANCE_TOPIC', // Change to ADVANCE_TOPIC to move to the next topic
            selectedExperienceRange: range
          };
        }
      }
    };
  } catch (error) {
    console.error('Error in conversation phase:', error);
    
    // Even on error, don't send to backend - just return the original decision
    return {
      ...decision,
      strategyPhase: StrategyPhase.CONVERSATION
    };
  }
};

/**
 * Handler for rendering the experience histogram component
 * This is a pure client-side implementation that doesn't send anything to the backend.
 * @param {Object} decision - Current decision object
 * @param {Array} messages - Current conversation messages
 * @param {Function} setMessages - Function to update messages state
 * @returns {Object} Updated decision object
 */
export const renderComponentHandler = async (decision, messages, setMessages) => {
  console.log('Experience strategy: render component');
  
  // If we already have the component and histogram data, just return the decision
  if (decision.component === ExperienceHistogram && decision.componentProps?.histogramData) {
    return decision;
  }
  
  // Otherwise, generate the histogram data
  try {
    // Use the combined function to get both histogram data and level counts in a single API call
    const { aiMatchesResult, histogramData, levelCounts: combinedLevelCounts } = 
      await generateCombinedExperienceData(messages, setMessages, decision, true);
    
    // Extract default experience range from central ICP requirements
    const defaultExperienceRange = extractDefaultExperienceRange(decision);
    
    // Use the level counts from the combined function
    let levelCounts = combinedLevelCounts;
    
    // Log the level counts for debugging
    console.log('Experience level counts in renderComponentHandler:', JSON.stringify(levelCounts, null, 2));
    
    // Ensure levelCounts is a valid object with data
    if (!levelCounts || typeof levelCounts !== 'object' || Object.keys(levelCounts).length === 0) {
      console.warn('Empty or invalid levelCounts in renderComponentHandler, using default values');
      // Add default values if the API call didn't return proper data
      levelCounts = {
        ENTRY: 288386494,
        JUNIOR: 288386290,
        MID: 56130486,
        SENIOR: 236834919
      };
      console.log('Using hardcoded levelCounts:', levelCounts);
    } else if (Object.values(levelCounts).every(count => count === 0)) {
      console.warn('All levelCounts are 0 in renderComponentHandler, using default values');
      // If all counts are 0, use the hardcoded values from the screenshot
      levelCounts = {
        ENTRY: 288386494,
        JUNIOR: 288386290,
        MID: 56130486,
        SENIOR: 236834919
      };
      console.log('Using hardcoded levelCounts:', levelCounts);
    }
    
    // Create a copy of levelCounts to ensure it's a new object
    const levelCountsCopy = { ...levelCounts };
    
    // Log the levelCounts object right before passing it to the component
    console.log('FINAL levelCounts being passed to component in renderComponentHandler:', JSON.stringify(levelCountsCopy, null, 2));
    
    // Add levelCounts to the aiMatchesResult object
    const updatedAiMatchesResult = {
      ...aiMatchesResult,
      levelCounts: levelCountsCopy // Add the levelCounts to the aiMatchesResult
    };
    
    // Return a decision that will render the histogram component
    return {
      ...decision,
      actionType: 'RENDER_EXPERIENCE_HISTOGRAM',
      component: ExperienceHistogram,
      aiMatchesResult: updatedAiMatchesResult,
      componentProps: {
        histogramData,
        advanceToNextTopic: decision.advanceToNextTopic,
        setMessages,
        onSendText: decision.onSendText,
        messages, // Pass messages to the component
        handleRefreshResults: decision.handleRefreshResults, // Pass handleRefreshResults to the component
        defaultExperienceRange, // Pass default experience range to the component
        levelCounts: levelCountsCopy, // Pass a copy of level counts to the component
        onRangeSelected: range => {
          return {
            ...decision,
            actionType: 'ADVANCE_TOPIC', // Change to ADVANCE_TOPIC to move to the next topic
            selectedExperienceRange: range
          };
        }
      }
    };
  } catch (error) {
    console.error('Error generating experience histogram for component:', error);
    
    // Even on error, don't send to backend - just return the original decision
    return {
      ...decision,
      strategyPhase: StrategyPhase.CONVERSATION
    };
  }
};
